import type { AxiosRequestConfig } from 'axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { AUDIENCE_CSV_IMPORT_TYPE } from '@/constants/audiences';
import { REQUEST_METHOD } from '@/constants/common';
import {
  IAudienceCompanyCsvDataUploadedItem,
  IAudienceCsvDataUploadedPagination,
  IAudienceImagesDataUploadItem,
  IAudienceItem,
  CampaignTemplate,
  IAudiencePersonCsvDataUploadedItem,
  ICheckCustomerMatchesResponse,
  ICheckCrossAudienceResponse,
  ICopyWorkflowBody,
  ICopyWorkflowResponse,
  ICreateAudienceBody,
  IUpdateAudienceBody,
  IFact,
  IGetAvailableWorkflowsResponse,
  TriggerFilter,
  IAudienceTriggerWebhook,
} from '@/types/audiences';
import { apiInstance } from '@/utils';
import type { CustomerFilterMode } from '@/services/gtmSignals';
import type { EnrichProvider, EnrichableField } from '@/lib/enrichment';

export const getAllFacts = async (): Promise<IFact[]> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAllFacts,
  });

  return result.data;
};

// #3365: create a webhook-source audience (payload-as-data, AI-mapped). Returns the
// inbound URL + one-time signing secret. creation_method stays 'scheduled_import';
// rules[0].source='webhook' distinguishes it.
export interface CreateWebhookAudienceResponse {
  message: string;
  audienceId: number;
  listId: number;
  endpoint_url: string;
  secret: string;
  run_mode: 'on_insert' | 'on_schedule';
}

export const createWebhookAudience = async (data: {
  name: string;
  run_mode: 'on_insert' | 'on_schedule';
  schedule_config?: Record<string, unknown> | null;
  timezone?: string | null;
}): Promise<CreateWebhookAudienceResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.createAudience,
    data: {
      name: data.name,
      creation_method: 'scheduled_import',
      type: 'person',
      rules: [{ source: 'webhook', run_mode: data.run_mode }],
      ...(data.run_mode === 'on_schedule'
        ? { schedule_config: data.schedule_config, timezone: data.timezone }
        : {}),
    },
  });

  return result.data;
};

/** One uploaded company: a name, a website, or both. The website is what Salesforce matches on. */
export interface ISalesforceCompanyListRow {
  name: string | null;
  website: string | null;
}

/** Settings for an audience built from an uploaded company list + Salesforce (mb-server#6278). */
export interface ISalesforceCompanyListSettings {
  companies: ISalesforceCompanyListRow[];
  include_contacts: boolean;
  include_leads: boolean;
  /** Empty = everyone at the company; otherwise people whose Title contains one of these. */
  title_keywords: string[];
  exclude_customers: boolean;
  exclude_open_opportunities: boolean;
}

export type SalesforceCompanyResultStatus =
  | 'imported' | 'no_match' | 'name_only' | 'no_people' | 'customer' | 'open_opportunity';

export interface ISalesforceCompanyResult {
  name: string | null;
  website: string | null;
  /** 'name_only' is only on runs from before name matches were imported. */
  status: SalesforceCompanyResultStatus;
  /** How the company was found in Salesforce; null when it was not. Absent on older runs. */
  matched_by?: 'website' | 'name' | null;
  /** The name matched several Salesforce accounts and every one was imported — check and delete the wrong ones. */
  ambiguous?: boolean;
  accounts: { id: string; name: string; website: string | null; /** People imported from this account. */ people?: number }[];
  contacts: number;
  leads: number;
}

export interface ISalesforceCompanyListImport {
  status: 'running' | 'completed' | 'failed';
  error: string | null;
  summary: {
    companies_requested: number;
    companies_imported: number;
    companies_no_match: number;
    /** Only on runs from before name matches were imported. */
    companies_name_only?: number;
    /** Matched on the name because the website found nothing (or none was given). */
    companies_name_matched?: number;
    /** Name matched several accounts; all imported and flagged for review. */
    companies_ambiguous?: number;
    companies_no_people: number;
    customers_skipped: number;
    open_opportunities_skipped: number;
    contacts_found: number;
    leads_found: number;
    duplicates_skipped: number;
    people_imported: number;
    /** Rows Salesforce returned that were not valid people (e.g. no name or email). */
    people_invalid?: number;
    /** Rows that failed to save. */
    people_failed?: number;
    cap_reached: boolean;
    max_people: number;
  } | null;
  results: ISalesforceCompanyResult[] | null;
}

/** Create a person audience from an uploaded company list; the import runs in the background. */
export const createSalesforceCompanyListAudience = async (
  name: string,
  settings: ISalesforceCompanyListSettings,
): Promise<{ audienceId: number }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: {
      name, permissions: 'user', type: 'person', creation_method: 'salesforce', salesforce_company_list: settings,
    },
    url: API_ENDPOINTS.createAudience,
  });
  return result.data;
};

/** How a company-list import went, per company. */
export const getSalesforceCompanyListImport = async (
  audienceId: number,
): Promise<ISalesforceCompanyListImport> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.salesforceCompanyListImport(audienceId),
  });
  return result.data;
};

export const createAudience = async (
  data: ICreateAudienceBody,
): Promise<{
  audienceId: number;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.createAudience,
  });

  return result.data;
};

export const getAllAudiences = async (params?: {
  page?: number;
  limit?: number;
  type?: 'company' | 'person';
  creation_method?: string[];
  workflow_status?: 'has_workflows' | 'no_workflows';
  published_status?: 'published' | 'not_published';
  trigger_type?: 'manual' | 'webhook';
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  owner_user_id?: string;
  view?: 'campaign_picker' | 'audience_picker' | 'power_dialer_picker';
  shared_with_team?: boolean;
  tags?: string[];
}): Promise<{
  audiences: IAudienceItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}> => {
  // Build query parameters
  const queryParams: Record<string, string | number | boolean> = {};

  if (params?.page) queryParams.page = params.page;
  if (params?.limit) queryParams.limit = params.limit;
  if (params?.type) queryParams.type = params.type;
  if (params?.creation_method && params.creation_method.length > 0) {
    queryParams.creation_method = params.creation_method.join(',');
  }
  if (params?.workflow_status) queryParams.workflow_status = params.workflow_status;
  if (params?.published_status) queryParams.published_status = params.published_status;
  if (params?.trigger_type) queryParams.trigger_type = params.trigger_type;
  if (params?.search) queryParams.search = params.search;
  if (params?.sortBy) queryParams.sortBy = params.sortBy;
  if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;
  if (params?.owner_user_id) queryParams.owner_user_id = params.owner_user_id;
  if (params?.view) queryParams.view = params.view;
  if (params?.shared_with_team) queryParams.shared_with_team = params.shared_with_team;
  if (params?.tags && params.tags.length > 0) queryParams.tags = params.tags.join(',');

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAllAudiences,
    params: queryParams,
  });

  return {
    audiences: result.data?.audiences || [],
    pagination: result.data?.pagination || {
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0,
    },
  };
};

export const getAudienceTags = async (scope: 'mine' | 'team'): Promise<{ tags: string[] }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAudienceTags,
    params: { scope },
  });
  return result.data;
};

export interface IRecommendedAudience {
  id: number;
  name: string;
  type: string;
  creation_method: string;
  people_count: number | null;
  updated_at: string;
  score: number;
  reason: string;
}

export const getRecommendedAudiences = async (): Promise<{ recommended: IRecommendedAudience[] }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getRecommendedAudiences,
  });
  return result.data;
};

export const deleteAudience = async (audienceId: number) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.deleteAudience(audienceId),
  });

  return result.data;
};

export const updateAudience = async (audienceId: number, data: IUpdateAudienceBody) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    data,
    url: API_ENDPOINTS.updateAudience(audienceId),
  });

  return result.data;
};

// #3884 — append a found-people audience into an existing campaign and trigger it.
export interface AddToCampaignResponse {
  message: string;
  added: number;
  skipped: number;
  targetAudienceId: number;
  targetName: string;
}
export const addAudienceToCampaign = async (
  audienceId: number,
  targetAudienceId: number,
): Promise<AddToCampaignResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: { targetAudienceId },
    url: API_ENDPOINTS.addAudienceToCampaign(audienceId),
  });

  return result.data;
};

export const searchAudiences = async (query: string): Promise<{ id: number; name: string }[]> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    params: {
      query: query || '',
      excludeWithActivePlays: true,
    },
    url: API_ENDPOINTS.searchAudiences,
  });

  return result.data?.audiences;
};

export const uploadCsvForAudience = async (data: {
  type: AUDIENCE_CSV_IMPORT_TYPE;
  data: any;
  template?: any;
  list_id?: number; // Optional: for updating existing audience
  audienceId?: number; // Optional: for tracking which audience is being updated
}): Promise<{ message: string; list_id: number; mode: 'create' | 'append' }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.uploadCsvForAudience,
  });

  return result.data;
};

export const importWebsiteVisits = async (data: {
  type: 'person' | 'company';
  signalType?: 'website' | 'ad-viewers' | 'all';
  timeFilter?: string | null; // null means no time constraint (within last N days)
  minDaysAgo?: number | null; // For "older than N days" filtering (greaterThanInclusive)
  filters?: {
    excludeCustomers?: boolean;
    /** Shared existing-customer definition (mb-server#6451); applies on top of `excludeCustomers`. */
    customers?: CustomerFilterMode;
    industries?: string[];
    companySizes?: string[];
    countries?: string[];
    campaigns?: string[];
    deviceTypes?: string[];
    pageUrls?: string[];
    namedAccountsOnly?: boolean;
    chatToken?: string;
    search?: string;
  };
  maxRecords?: number;
  audienceId?: number; // Optional: if provided, update existing audience with new list_id and filters
}): Promise<{
  message: string;
  list_id: number;
  summary: {
    totalSignalsFetched: number;
    transformedCount: number;
    validRowsInserted: number;
    rowsSkipped: number;
    skippedReasons: Record<string, number>;
  };
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.importWebsiteVisits,
  });

  return result.data;
};

// GH#3538: People discovered via signal tasks (gtm_signal_personas) at given
// company domains, in the same signal-row shape used by the visits preview.
export interface WebsiteVisitTaskPerson {
  id: string;
  has_person_info: boolean;
  signal_source: 'signal_task';
  contact_name: string;
  contact_email: string;
  contact_title: string;
  phone: string;
  linkedin_url: string;
  company_name: string;
  company_domain: string;
}

export const getWebsiteVisitTaskPeople = async (
  domains: string[],
  signal?: AbortSignal,
): Promise<{ people: WebsiteVisitTaskPerson[] }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: { domains },
    url: API_ENDPOINTS.websiteVisitTaskPeople,
    signal,
  });

  return result.data;
};

/**
 * Workspace rollout flags carried on every audience rows payload.
 *
 * Response-level rather than per-row: the flag is a property of the workspace,
 * and the grid needs it even on a page where every account happens to be
 * unscored — otherwise a sparsely covered audience would hide the Account
 * Priority column instead of showing it full of Unknown.
 */
export interface AudienceMemberFeatures {
  accountPriorityEnabled?: boolean;
}

export const getAudienceCsvOrImagesDataUploaded = async (data: {
  list_id: number;
  type: AUDIENCE_CSV_IMPORT_TYPE;
  params: {
    page?: number;
    limit?: number;
    row_ids?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    // #2223: server-side boolean research-column filter.
    researchFilterColumn?: number;
    researchFilterValue?: 'true' | 'false' | 'unanswered';
    // Power Dialer queue: only rows with a non-blank phone.
    has_phone?: 'true';
    // Find People grid: drop company rows the contact finder matched no person for.
    hide_not_found?: 'true';
    contact_filter?: 'found' | 'hide_not_found';
  };
}, request: Pick<AxiosRequestConfig, 'signal' | 'timeout'> = {}): Promise<{
  pagination: IAudienceCsvDataUploadedPagination;
  rows:
  | IAudiencePersonCsvDataUploadedItem[]
  | IAudienceCompanyCsvDataUploadedItem[]
  | IAudienceImagesDataUploadItem[];
  source: 'csv' | 'pending';
  features?: AudienceMemberFeatures;
}> => {
  const result = await apiInstance({
    ...request,
    method: REQUEST_METHOD.GET,
    params: data?.params,
    url: API_ENDPOINTS.getAudienceCsvOrImagesDataUploaded(data.type, data.list_id),
  });

  return result.data;
};

export interface AudienceAttributeColumnMetadata {
  key: string;
  label: string | null;
  data_type: string;
  order: number;
  source: 'manifest' | 'discovered';
}

/**
 * Audience-level scalar attribute metadata. The server unions declared fields with
 * bounded list discovery so columns do not depend on the current member page.
 */
export const getAudienceAttributeColumns = async (data: {
  audience_id: number;
  list_id: number;
  type: AUDIENCE_CSV_IMPORT_TYPE;
}): Promise<{ columns: AudienceAttributeColumnMetadata[] }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    params: { audience_id: data.audience_id },
    url: API_ENDPOINTS.getAudienceAttributeColumns(data.type, data.list_id),
  });
  return result.data;
};

/**
 * POST companion to getAudienceCsvOrImagesDataUploaded for large id sets.
 * Same response shape, but ids go in the request body so we can pass
 * thousands of them without hitting URL/header size limits.
 *
 * Used by the Audience Detail "Only matching" toggle when a list check
 * (customers / cross-audience overlap / outreach status) yields hundreds
 * or thousands of matched row ids. The GET ?row_ids= path tops out around
 * a few hundred ids before nginx returns HTTP 431.
 */
export const getAudienceRowsByIds = async (data: {
  list_id: number;
  type: AUDIENCE_CSV_IMPORT_TYPE;
  ids: number[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}, request: Pick<AxiosRequestConfig, 'signal' | 'timeout'> = {}): Promise<{
  pagination: IAudienceCsvDataUploadedPagination;
  rows:
  | IAudiencePersonCsvDataUploadedItem[]
  | IAudienceCompanyCsvDataUploadedItem[]
  | IAudienceImagesDataUploadItem[];
  source: 'csv' | 'pending';
  features?: AudienceMemberFeatures;
}> => {
  const result = await apiInstance({
    ...request,
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.getAudienceRowsByIds(data.type, data.list_id),
    params: {
      ...(data.sortBy ? { sortBy: data.sortBy } : {}),
      ...(data.sortOrder ? { sortOrder: data.sortOrder } : {}),
    },
    data: { ids: data.ids },
  });
  return result.data;
};

export const getAudienceMatches = async (
  audienceId: number,
  params: {
    page?: number; limit?: number; search?: string; sortBy?: string; sortOrder?: 'asc' | 'desc';
    /** A loop audience's entry-state filter. */
    loop_state?: string;
  },
  request: Pick<AxiosRequestConfig, 'signal' | 'timeout'> = {},
): Promise<{
  rows: Record<string, unknown>[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  features?: AudienceMemberFeatures;
}> => {
  const result = await apiInstance({
    ...request,
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAudienceMatches(audienceId),
    params,
  });
  return result.data;
};

/**
 * Progress of an on-demand account fit run.
 *
 * `completedCompanies` is derived server-side from outstanding claims rather
 * than a counter, so a lost or replayed queue message cannot drift it.
 */
export interface AccountFitRun {
  requestId: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  totalCompanies: number;
  skippedCompanies: number;
  completedCompanies: number;
  failedCompanies: number;
  creditsCharged: number;
  lastError: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface AccountFitRunStart {
  requestId: string | null;
  dispatched: number;
  skipped: number;
  inFlight: number;
  total: number;
  creditsEstimated?: number;
  message: string;
}

/**
 * Score every DISTINCT company in an audience against the workspace ICP.
 *
 * Companies, not rows — a 513-person list is typically ~134 companies, and both
 * the work and the billing are per company. Results land on the shared Account
 * Priority projection, so they appear in this grid's Account Priority column and
 * on Prospects, Tasks and Signals at the same time.
 */
export const runAccountFit = async (
  audienceId: number,
  params: { force?: boolean } = {},
): Promise<AccountFitRunStart> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.runAccountFit(audienceId),
    data: params,
  });
  return result.data;
};

export const getAccountFitStatus = async (
  audienceId: number,
): Promise<{ run: AccountFitRun | null }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAccountFitStatus(audienceId),
  });
  return result.data;
};

/**
 * What a run would evaluate, and what it would cost — all in companies.
 *
 * `pending` is what a normal run does; `rescore` is what a forced one does. They
 * differ by the companies already carrying a recent fit, which is exactly the
 * number a user needs before choosing between the two.
 */
export interface AccountFitEstimate {
  /** Distinct companies this audience resolves to. */
  total: number;
  /** Companies a normal run would evaluate — no recent fit, not already claimed. */
  pending: number;
  /** Companies skipped for already having a fit from the last 30 days. */
  fresh: number;
  /** Companies a run is already working; never dispatched twice, force or not. */
  inFlight: number;
  /** Companies a forced re-score would evaluate. */
  rescore: number;
  /** Credit rate per company, from the server's own pricing catalogue. */
  creditsPerCompany: number;
}

/**
 * Asked when the run menu opens, never polled: it re-resolves the audience's
 * distinct companies, which is the same scan a run does.
 */
export const getAccountFitEstimate = async (
  audienceId: number,
): Promise<{ estimate: AccountFitEstimate }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAccountFitEstimate(audienceId),
  });
  return result.data;
};

export const getEmptyRows = async (data: {
  list_id: number;
  type: AUDIENCE_CSV_IMPORT_TYPE;
  field: string;
}): Promise<{ rows: Record<string, unknown>[]; total: number }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getEmptyRows(data.type, data.list_id, data.field),
  });

  return result.data;
};

/** Row scopes the enrichment menu offers. 'retry' = the catch-all + unverified pool. */
export type EnrichRowScope = 'empty' | 'retry';

export interface IEnrichSummary {
  field: EnrichableField;
  /** Size of each scope's FULL pool, before any provider's already-tried rows are removed. */
  scopes: Record<EnrichRowScope, number>;
  /** Every email state, for the footer legend. */
  statuses: {
    empty: number;
    valid: number;
    catchall: number;
    unverified: number;
    /** Typed in by a person — counted, but in no retry scope. */
    manual: number;
  };
  /**
   * Company-type audiences only: companies with zero discovered contacts at all (the
   * grid's "Not found" placeholder rows). Excluded from `statuses`/`scopes`/`providers`
   * on purpose — there is no email to grade — but the grid's own row-count total DOES
   * include them, so the footer legend needs this to reconcile against it. Always 0 for
   * person-type audiences.
   */
  notFound: number;
  /**
   * Per provider, the rows in each scope it has NOT already been run on. Null when the
   * caller asked for the cheap half only — absent, not zero, since a zero reads as
   * "exhausted".
   */
  providers: Record<EnrichProvider, Record<EnrichRowScope, number>> | null;
}

/**
 * Counts behind the column ⋯ enrichment menu. One call feeds the checkbox counts, the
 * "Enrich all · N" total, the "already tried" note and each provider's eligible count,
 * so toggling a checkbox needs no round trip and the numbers can't disagree.
 */
export const getEnrichSummary = async (data: {
  list_id: number;
  type: AUDIENCE_CSV_IMPORT_TYPE;
  field: EnrichableField;
  /**
   * Per-provider eligibility costs an attempt-log scan over the whole list. The footer
   * legend needs only the bucket totals, so it asks for the cheap half; the menu, the one
   * surface that needs eligibility, asks for everything.
   */
  includeProviders?: boolean;
}): Promise<IEnrichSummary> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getEnrichSummary(data.type, data.list_id, data.field),
    ...(data.includeProviders === false ? { params: { providers: '0' } } : {}),
  });

  return result.data;
};

export interface IEmailSuppressionSummary {
  /** Distinct non-empty addresses on the list. */
  withEmail: number;
  /** Of those, how many will never receive an email touch because they opted out. */
  suppressed: number;
  /** Suppressed by a MarketBetter unsubscribe, reply, or manual Do Not Contact. */
  marketbetterUnsubscribed: number;
  /** Suppressed by a CRM opt-out. Overlaps the count above where an address is on both. */
  crmOptedOut: number;
  /**
   * CRM opt-outs that are NOT also MarketBetter unsubscribes — the disjoint remainder, so
   * `marketbetterUnsubscribed + crmOnly === suppressed`. This is the number worth telling
   * an operator, because it is the part they have to go and fix in their CRM.
   */
  crmOnly: number;
}

/**
 * How many of a list's contacts have opted out of email (mb-server#5493).
 *
 * Read once when the run modal opens, to qualify the pace summary's "All N contacts will
 * be contacted" — the send path refuses the suppressed ones and, before this, nothing said
 * so until the numbers came back short.
 */
export const getEmailSuppressionSummary = async (data: {
  list_id: number;
  type: AUDIENCE_CSV_IMPORT_TYPE;
}): Promise<IEmailSuppressionSummary> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getEmailSuppressionSummary(data.type, data.list_id),
  });

  return result.data;
};

/**
 * Start a run: claim the rows this provider may still touch, and get a run id back.
 *
 * Claiming rather than listing is what stops two tabs from dispatching — and paying for —
 * the same rows. Anything the caller does not go on to dispatch must be handed back with
 * releaseEnrichClaims, or it stays claimed until the claim expires.
 */
export const startEnrichRun = async (data: {
  list_id: number;
  type: AUDIENCE_CSV_IMPORT_TYPE;
  field: EnrichableField;
  provider: EnrichProvider;
  scopes: EnrichRowScope[];
  /** Omit for "all eligible". */
  limit?: number;
  /**
   * Enrich only these rows ("Enrich selected"). The server still applies tenant, list and
   * eligibility rules, so an id that is not eligible simply does not come back.
   */
  rowIds?: number[];
}): Promise<{ runId: string; rows: Record<string, unknown>[]; total: number }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.startEnrichRun(data.type, data.list_id, data.field),
    data: {
      provider: data.provider,
      scopes: data.scopes,
      ...(data.limit ? { limit: data.limit } : {}),
      ...(data.rowIds ? { rowIds: data.rowIds } : {}),
    },
  });

  return result.data;
};

/** Hand back rows a run claimed but never dispatched. */
export const releaseEnrichClaims = async (data: {
  list_id: number;
  type: AUDIENCE_CSV_IMPORT_TYPE;
  field: EnrichableField;
  provider: EnrichProvider;
  runId: string;
  rowIds: number[];
}): Promise<{ released: number }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.releaseEnrichClaims(data.type, data.list_id, data.field),
    data: { provider: data.provider, runId: data.runId, rowIds: data.rowIds },
  });

  return result.data;
};

export interface IEnrichOutcomes {
  valid: number;
  catchall: number;
  unverified: number;
  not_found: number;
  error: number;
  /** Rows still claimed with no answer — in flight, or lost. */
  pending: number;
  /** Everything that reached a terminal state. */
  settled: number;
  /** MB credits actually charged for this run, from the credit records. */
  creditsUsed: number | null;
}

/**
 * What a finished run produced, from the row attempt log.
 *
 * Read from the server rather than tallied client-side: results land asynchronously and
 * mostly through row polling, which only observes rows on the current page — a long run's
 * outcome is simply not visible from here.
 */
export const getEnrichOutcomes = async (data: {
  list_id: number;
  type: AUDIENCE_CSV_IMPORT_TYPE;
  field: EnrichableField;
  runId: string;
}): Promise<IEnrichOutcomes> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getEnrichOutcomes(data.type, data.list_id, data.field),
    params: { runId: data.runId },
  });

  return result.data;
};

export interface AudienceBuilderPerson {
  firstName?: string;
  lastName?: string;
  name?: string;
  title?: string | null;
  company?: string | null;
  companyDomain?: string | null;
  linkedinUrl?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface AudienceBuilderSearchResponse {
  companies: Array<{ name?: string; domain?: string | null }>;
  people: AudienceBuilderPerson[];
  credits: { companies: number; people: number; total: number };
  duration: number;
  audienceId: number | null;
  source: string;
  warnings?: Array<{ code: string; message: string }>;
}

export const searchAudienceByPrompt = async (data: {
  query: string;
  numberOfPeople: number;
  provider?: 'marketbetter';
}): Promise<AudienceBuilderSearchResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: {
      query: data.query,
      numberOfPeople: data.numberOfPeople,
      provider: data.provider ?? 'marketbetter',
    },
    url: '/private/audience-builder/search',
  });

  return result.data;
};

export const searchCompanyPeople = async (data: {
  companyName: string;
  jobTitles: string[];
  numberOfPeople?: number;
}): Promise<AudienceBuilderSearchResponse> => {
  try {
    const result = await apiInstance({
      method: REQUEST_METHOD.POST,
      data: {
        companyName: data.companyName,
        jobTitles: data.jobTitles,
        numberOfPeople: Math.min(data.numberOfPeople ?? 4, 4),
      },
      url: '/private/audience-builder/company-people-search',
    });

    return result.data;
  } catch (error) {
    const apiError = error as {
      data?: { error?: string };
      response?: { data?: { error?: string } };
    };
    const message = apiError.response?.data?.error || apiError.data?.error;
    if (message) throw new Error(message);
    throw error;
  }
};

export const deleteAllRowsByListId = async (data: {
  list_id: number;
  type: AUDIENCE_CSV_IMPORT_TYPE;
}) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.deleteAllRowsByListId(data.type, data.list_id),
  });

  return result.data;
};

const getRowById = async (data: {
  type: AUDIENCE_CSV_IMPORT_TYPE;
  list_id: number;
  id: number;
}): Promise<IAudiencePersonCsvDataUploadedItem | IAudienceCompanyCsvDataUploadedItem> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getRowById(data.type, data.list_id, data.id),
  });

  return result.data;
};

export const updateRowById = async (data: {
  type: AUDIENCE_CSV_IMPORT_TYPE;
  list_id: number;
  id: number;
  updates: Partial<IAudiencePersonCsvDataUploadedItem | IAudienceCompanyCsvDataUploadedItem>;
}): Promise<IAudiencePersonCsvDataUploadedItem | IAudienceCompanyCsvDataUploadedItem> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    url: API_ENDPOINTS.updateRowById(data.type, data.list_id, data.id),
    data: data.updates,
  });

  return result.data;
};

/**
 * Append a single person/company row to a list. The DB assigns the id; DO
 * NOT use updateRowById for "add" — it upserts by id and silently replaces
 * an existing row when the client-picked id collides (e.g. paginated lists
 * where the UI only knows the current page's ids).
 */
export const insertSingleRow = async (data: {
  type: AUDIENCE_CSV_IMPORT_TYPE;
  list_id: number;
  fields: Partial<IAudiencePersonCsvDataUploadedItem | IAudienceCompanyCsvDataUploadedItem>;
}): Promise<IAudiencePersonCsvDataUploadedItem | IAudienceCompanyCsvDataUploadedItem> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.insertSingleRow(data.type, data.list_id),
    data: data.fields,
  });

  return result.data;
};

export const deleteRowById = async (data: {
  type: AUDIENCE_CSV_IMPORT_TYPE;
  list_id: number;
  id: number;
}) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.deleteRowById(data.type, data.list_id, data.id),
  });

  return result.data;
};

/**
 * Remove many rows in one request.
 *
 * Bulk removals used to loop `deleteRowById` once per row. The backend's
 * per-user API rate limiter allows 30 requests per minute per endpoint, so any
 * removal past 30 rows started coming back 429 and the grid reported
 * "Failed to remove N of N" — the rows really were still there. Use this for
 * anything that removes more than a single row.
 *
 * Chunked because the endpoint caps a request at 5000 ids.
 */
/** True when a rejected request was throttled by the API rate limiter. */
export const isRateLimitedError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 429;

export const bulkDeleteRows = async (data: {
  type: AUDIENCE_CSV_IMPORT_TYPE;
  list_id: number;
  ids: number[];
}): Promise<{ deleted: number; requested: number; ids: number[] }> => {
  const CHUNK = 2000;
  const unique = Array.from(new Set(data.ids.filter((id) => Number.isInteger(id) && id > 0)));
  const summary = { deleted: 0, requested: unique.length, ids: [] as number[] };

  for (let start = 0; start < unique.length; start += CHUNK) {
    const chunk = unique.slice(start, start + CHUNK);
    // Sequential on purpose: parallel chunks would spend several rate-limit
    // points at once, which is the problem this endpoint exists to avoid.
    // eslint-disable-next-line no-await-in-loop
    const result = await apiInstance({
      method: REQUEST_METHOD.POST,
      url: API_ENDPOINTS.bulkDeleteRows(data.type, data.list_id),
      data: { ids: chunk },
    });
    summary.deleted += result.data?.deleted ?? 0;
    summary.ids.push(...(result.data?.ids ?? []));
  }

  return summary;
};

export const suppressContact = async (data: {
  audienceId: number;
  people_id?: number;
  company_id?: number;
  email?: string;
  linkedin_url?: string;
  company_domain?: string;
}) => {
  const { audienceId, ...body } = data;
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.suppressContact(audienceId),
    data: body,
  });

  return result.data;
};

export interface SuppressedContactRow {
  suppression_id: number;
  people_id: number | null;
  company_id: number | null;
  email: string | null;
  linkedin_url: string | null;
  company_domain: string | null;
  suppressed_at: string;
  source: string;
  // People fields (from JOIN)
  p_id: number | null;
  first_name: string | null;
  last_name: string | null;
  p_email: string | null;
  p_company_name: string | null;
  p_phone: string | null;
  p_job_title: string | null;
  p_website: string | null;
  p_linkedin_url: string | null;
  p_list_id: number | null;
  p_vendorid: number | null;
  // Company fields (from JOIN)
  c_id: number | null;
  company_name: string | null;
  c_email: string | null;
  c_phone: string | null;
  c_website: string | null;
  contact_first_name: string | null;
  contact_last_name: string | null;
  c_linkedin_url: string | null;
  c_list_id: number | null;
  c_vendorid: number | null;
}

export const getSuppressedContacts = async (audienceId: number, page = 1, limit = 50) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getSuppressedContacts(audienceId),
    params: { page, limit },
  });
  return result.data as {
    rows: SuppressedContactRow[];
    total: number;
    page: number;
    totalPages: number;
  };
};

export const unsuppressContact = async (audienceId: number, suppressionId: number) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.unsuppressContact(audienceId, suppressionId),
  });
  return result.data;
};

export const getAudienceById = async (id: number): Promise<{ audience: IAudienceItem }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAudienceById(id),
  });

  return result.data;
};

export interface DuplicateAudienceResponse {
  message: string;
  audienceId: number;
  memberCount: number;
  imageCount: number;
  researchColumnCount: number;
  researchCellCount: number;
}

export const duplicateAudience = async ({
  audienceId,
  name,
}: {
  audienceId: number;
  name: string;
}): Promise<DuplicateAudienceResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.duplicateAudience(audienceId),
    data: { name },
  });
  return result.data;
};

export const getAudienceCountries = async (): Promise<{ countries: string[] }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAudienceCountries,
  });
  return result.data;
};

export const getAudienceContinents = async (): Promise<{ continents: string[] }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAudienceContinents,
  });
  return result.data;
};

export const getAudienceStates = async (): Promise<{ states: string[] }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAudienceStates,
  });
  return result.data;
};

export const getAvailableWorkflows = async (): Promise<IGetAvailableWorkflowsResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAvailableWorkflows,
  });
  return result.data;
};

export const getSystemWorkflows = async (creationMethod?: string): Promise<IGetAvailableWorkflowsResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getSystemWorkflows,
    params: creationMethod ? { creationMethod } : undefined,
  });
  return result.data;
};

export const copyWorkflow = async (data: ICopyWorkflowBody): Promise<ICopyWorkflowResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.copyWorkflow,
    data,
  });
  return result.data;
};

export const checkCustomerMatches = async (data: {
  type: AUDIENCE_CSV_IMPORT_TYPE;
  list_id: number;
}): Promise<ICheckCustomerMatchesResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.checkCustomerMatches(data.type, data.list_id),
  });
  return result.data;
};

export interface ICrmEnrichResult {
  matched: number;
  filled: { email: number; phone: number; linkedin_url: number };
  byCrm: { hubspot: number; salesforce: number };
  creditsUsed: number;
  processed: number;
  freshness?: Record<string, { syncedAt: string | null; ageHours: number | null; fresh: boolean }>;
  skipped?: string;
  context?: {
    attributeKey: 'crm_context';
    processed: number;
    filled: number;
    byCrm: { hubspot: number; salesforce: number };
    skipped?: string;
  };
}

// Free CRM enrichment (HubSpot/Salesforce mirror). Bulk, set-based, no credits.
export const crmEnrichAudience = async (data: {
  audienceId: number;
  fields?: Array<'email' | 'phone' | 'linkedin_url'>;
  scope?: 'empty' | 'all';
  rowIds?: number[];
  /** Add relationship/deal history as the scalar `crm_context` audience attribute. */
  includeContext?: boolean;
}): Promise<ICrmEnrichResult> => {
  const { audienceId, ...body } = data;
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.crmEnrich(audienceId),
    data: body,
  });
  return result.data;
};

/**
 * Clear a campaign's "Needs attention" mark at what the rep saw — the failed-step count and the
 * row's `execution_stats.last_failure_at` token — or restore it with `failedSteps: null`. The
 * server lifts the clear on a higher count or any later failure. Only the Campaigns list reads
 * it; the campaign's run is untouched.
 */
export const clearCampaignAttention = async (
  audienceId: number,
  failedSteps: number | null,
  lastFailureAt: string | null,
): Promise<{ attention_cleared_failed_steps: number | null; attention_cleared_at: string | null }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.clearCampaignAttention(audienceId),
    data: { failed_steps: failedSteps, last_failure_at: failedSteps === null ? null : lastFailureAt },
  });
  return result.data;
};

export type LinkedInEnrichmentBatchProvider = 'fiber' | 'lusha' | 'apollo';
export type LinkedInEnrichmentBatchScope = 'ten' | 'all';
export type LinkedInEnrichmentBatchStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';
export type LinkedInEnrichmentBatchItemStatus =
  | 'queued'
  | 'dispatching'
  | 'processing'
  | 'enriched'
  | 'not_found'
  | 'failed';

export interface LinkedInEnrichmentBatchItem {
  person_id: number;
  request_id: string | null;
  status: LinkedInEnrichmentBatchItemStatus;
  linkedin_url: string | null;
  error: string | null;
  updated_at: string;
}

export interface LinkedInEnrichmentBatch {
  id: string;
  audience_id: number;
  list_id: number;
  provider: LinkedInEnrichmentBatchProvider;
  scope: LinkedInEnrichmentBatchScope;
  status: LinkedInEnrichmentBatchStatus;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  total: number;
  processed: number;
  enriched: number;
  not_found: number;
  failed: number;
  pending: number;
  skipped_recent_not_found?: number;
  items: LinkedInEnrichmentBatchItem[];
  pagination?: {
    limit: number;
    offset: number;
    total: number;
  };
}

export interface LinkedInEnrichmentBatchResponse {
  success: true;
  reused?: boolean;
  skippedRecentNotFound?: number;
  statusUrl?: string;
  batch: LinkedInEnrichmentBatch;
}

export const startLinkedInEnrichmentBatch = async (data: {
  audienceId: number;
  scope: LinkedInEnrichmentBatchScope;
  provider: LinkedInEnrichmentBatchProvider;
  idempotencyKey: string;
  personId?: number;
  /** Enrich only these people ("Enrich selected"); not combinable with personId. */
  personIds?: number[];
  forceRetry?: boolean;
}): Promise<LinkedInEnrichmentBatchResponse> => {
  const { audienceId, ...body } = data;
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.linkedinEnrichmentBatches(audienceId),
    data: body,
  });
  return result.data;
};

export const getLinkedInEnrichmentBatch = async (data: {
  audienceId: number;
  batchId: string;
  limit?: number;
  offset?: number;
}): Promise<LinkedInEnrichmentBatchResponse> => {
  const { audienceId, batchId, limit = 500, offset = 0 } = data;
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.linkedinEnrichmentBatch(audienceId, batchId),
    params: { limit, offset },
  });
  return result.data;
};

export type EmailEnrichmentBatchProvider = Exclude<EnrichProvider, 'crm'>;
export type EmailEnrichmentBatchStatus = LinkedInEnrichmentBatchStatus;
export type EmailEnrichmentBatchItemStatus = LinkedInEnrichmentBatchItemStatus;

export interface EmailEnrichmentBatchItem {
  row_id: number;
  request_id: string;
  status: EmailEnrichmentBatchItemStatus;
  email: string | null;
  email_validation_status: string | null;
  error: string | null;
  failure_stage: 'dispatch' | 'pre_provider' | 'processing' | null;
  updated_at: string;
}

export interface EmailEnrichmentBatch {
  id: string;
  audience_id: number;
  list_id: number;
  audience_type: 'person' | 'company';
  provider: EmailEnrichmentBatchProvider;
  scopes: EnrichRowScope[];
  requested_limit: number | null;
  retry_of_batch_id: string | null;
  status: EmailEnrichmentBatchStatus;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  total: number;
  processed: number;
  enriched: number;
  not_found: number;
  failed: number;
  retryable_failed: number;
  pending: number;
  items: EmailEnrichmentBatchItem[];
  pagination?: { limit: number; offset: number; total: number };
}

export interface EmailEnrichmentBatchResponse {
  success: true;
  reused?: boolean;
  statusUrl?: string;
  batch: EmailEnrichmentBatch;
}

export const startEmailEnrichmentBatch = async (data: {
  audienceId: number;
  provider: EmailEnrichmentBatchProvider;
  scopes: EnrichRowScope[];
  idempotencyKey: string;
  limit?: number;
  retryFailedBatchId?: string;
  /** Enrich only these rows ("Enrich selected"); not combinable with limit or retryFailedBatchId. */
  rowIds?: number[];
}): Promise<EmailEnrichmentBatchResponse> => {
  const { audienceId, ...body } = data;
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.emailEnrichmentBatches(audienceId),
    data: body,
  });
  return result.data;
};

export const getEmailEnrichmentBatch = async (data: {
  audienceId: number;
  batchId: string;
  limit?: number;
  offset?: number;
}): Promise<EmailEnrichmentBatchResponse> => {
  const {
    audienceId, batchId, limit = 500, offset = 0,
  } = data;
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.emailEnrichmentBatch(audienceId, batchId),
    params: { limit, offset },
  });
  return result.data;
};

export const checkCrossAudienceOverlap = async (data: {
  type: AUDIENCE_CSV_IMPORT_TYPE;
  list_id: number;
}): Promise<ICheckCrossAudienceResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.checkCrossAudienceOverlap(data.type, data.list_id),
  });
  return result.data;
};

export interface IOutreachStatusSummary {
  total: number;
  replied: number;
  bounced: number;
  sent: number;
  failed: number;
  pending: number;
}

export interface ICheckOutreachStatusResponse {
  statuses: Record<number, string>;
  summary: IOutreachStatusSummary;
}

export const checkOutreachStatus = async (data: {
  audienceId: number;
  type: AUDIENCE_CSV_IMPORT_TYPE;
  list_id: number;
}): Promise<ICheckOutreachStatusResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.checkOutreachStatus(data.audienceId),
    params: { type: data.type, list_id: data.list_id },
  });
  return result.data;
};

export interface LinkedInCommenterPreview {
  linkedin_url: string;
  name: string;
  first_name: string;
  last_name: string;
  linkedin_slug: string;
  comment_text: string;
  comment_count: number;
}

export const previewLinkedInCommenters = async (
  linkedin_post_url: string,
): Promise<{ commenters: LinkedInCommenterPreview[]; has_more: boolean; count: number }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: { linkedin_post_url },
    url: API_ENDPOINTS.previewLinkedInCommenters,
  });
  return result.data;
};

export const importLinkedInCommenters = async (
  linkedin_post_url: string,
): Promise<{ list_id: number; type: 'person'; imported_count: number; message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: { linkedin_post_url },
    url: API_ENDPOINTS.importLinkedInCommenters,
  });
  return result.data;
};

/** Why people in an audience were left out of a non-responder import, by reason. */
export interface INonRespondersExcluded {
  not_emailed: number;
  replied: number;
  bounced: number;
  unsubscribed: number;
  left_company: number;
  invalid_email: number;
  still_in_outreach: number;
  emailed_recently: number;
  no_email: number;
  duplicate: number;
}

/** People an audience emailed who never responded and can be emailed again. */
export interface INonRespondersSummary {
  audience_id: number;
  audience_name: string;
  total: number;
  eligible: number;
  excluded: INonRespondersExcluded;
}

export const previewNonResponders = async (audienceId: number): Promise<INonRespondersSummary> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.previewNonResponders(audienceId),
  });
  return result.data;
};

/** Copies the eligible people into a new list. `list_id` is null when nobody qualified. */
export const importNonResponders = async (
  audienceId: number,
): Promise<INonRespondersSummary & { list_id: number | null; type: 'person'; imported_count: number }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.importNonResponders(audienceId),
  });
  return result.data;
};

// TAM Builder (Fiber NL company search)

export interface TamSearchResult {
  id: number;
  vendor_id: number;
  query_text: string;
  // existingCustomersExcluded/existingCustomersAudienceId are stamped on completion --
  // companies whose domain already matched the vendor's own customers table were routed
  // to that accumulating list instead of this search's audience (a wasted find otherwise).
  search_params: Record<string, unknown> & {
    existingCustomersExcluded?: number;
    existingCustomersAudienceId?: number | null;
  };
  audience_id: number | null;
  companies_found: number | null;
  credits_used: number | null;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'preview_complete';
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export const createTamSearch = async (data: {
  query: string;
  pageSize?: number;
  relevanceCutoff?: number;
  maxPages?: number;
}): Promise<{ success: boolean; searchId: number; status: string; message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.createTamSearch,
  });
  return result.data;
};

export const getTamSearch = async (
  searchId: number,
): Promise<{ success: boolean; data: TamSearchResult }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getTamSearch(searchId),
  });
  return result.data;
};

// ─── Audience Webhook Trigger ────────────────────────────────────────────────

export const provisionAudienceWebhook = async (
  audienceId: number,
): Promise<{ endpoint_url: string; secret?: string; has_secret?: boolean }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.audienceWebhookProvision(audienceId),
  });
  return result.data;
};

// #3365: rotate the webhook signing secret (recover/revoke). Returns the new secret once.
export const regenerateAudienceWebhookSecret = async (
  audienceId: number,
): Promise<{ secret: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.audienceWebhookRegenerateSecret(audienceId),
  });
  return result.data;
};

// #3365: AI-infer the field mapping from the latest captured payload (preview only).
export const getAudienceWebhookAiMappingPreview = async (
  audienceId: number,
): Promise<{
  received: boolean;
  detected_type?: string;
  field_mappings: Record<string, string>;
  sample: Record<string, unknown> | null;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.audienceWebhookAiMappingPreview(audienceId),
  });
  return result.data;
};

// #3365: persist a manual field mapping override (mode='manual' wins over AI).
export const updateAudienceWebhookMapping = async (
  audienceId: number,
  mapping: { field_mappings: Record<string, string>; detected_type?: string; payload_path?: string },
): Promise<{ success: boolean }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    url: API_ENDPOINTS.audienceWebhookMapping(audienceId),
    data: mapping,
  });
  return result.data;
};

export const getAudienceWebhookConfig = async (
  audienceId: number,
): Promise<{
  trigger_type: string | null;
  trigger_filter: TriggerFilter | null;
  webhook: IAudienceTriggerWebhook | null;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.audienceWebhook(audienceId),
  });
  return result.data;
};

// Reveal the webhook secret (the x-mb-api-key value) for an already-provisioned audience webhook.
// The backend decrypts it only when reveal=true is passed. Returns null if none/not provisioned.
export const revealAudienceWebhookSecret = async (audienceId: number): Promise<string | null> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: `${API_ENDPOINTS.audienceWebhook(audienceId)}?reveal=true`,
  });
  return result.data?.webhook?.access_key ?? null;
};

export const updateAudienceWebhookFilter = async (
  audienceId: number,
  filter: TriggerFilter | null,
): Promise<{ success: boolean }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    url: API_ENDPOINTS.audienceWebhookFilter(audienceId),
    data: { filter },
  });
  return result.data;
};

export const deleteAudienceWebhook = async (
  audienceId: number,
): Promise<{ success: boolean }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.audienceWebhook(audienceId),
  });
  return result.data;
};

export const getAudienceWebhookDetectedFields = async (
  audienceId: number,
): Promise<{ fields: string[] }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.audienceWebhookDetectedFields(audienceId),
  });
  return result.data;
};

export const getAudienceWebhookActivity = async (
  audienceId: number,
): Promise<{
  last_fired_at: string | null;
  triggers_this_week: number;
  sparkline: number[];
  success_rate_24h: number | null;
  recent_errors: Array<{ code: number | null; message: string; source: string | null; timestamp: string }>;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.audienceWebhookActivity(audienceId),
  });
  return result.data;
};

export const getCampaignTemplates = async (
  scope: 'mine' | 'team', page: number, signal?: AbortSignal,
): Promise<{ audiences: CampaignTemplate[]; pagination: { total: number; page: number; totalPages: number } }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getAllAudiences,
    params: {
      view: 'campaign_picker', page, limit: 60,
      workflow_status: 'has_workflows', sortBy: 'updated_at', sortOrder: 'desc',
      ...(scope === 'team' ? { owner_user_id: 'all', shared_with_team: true } : {}),
    },
    timeout: 20000,
    signal,
  });
  return result.data;
};
